<template>
  <div class="home-container bsp-page">
    <div class="home bsp-page__content" v-if="!loading">
      <div class="client-view" v-if="currentUser && currentEstablishment">
        <BspPageHeader
          class="bsp-page-header--welcome"
          :breadcrumbs="accountBreadcrumbs"
          :title="`Hello, ${currentUser.name}`"
          :description="`Manage the accounts and phone numbers for ${currentEstablishment.name}.`"
          eyebrow="WhatsApp operations"
          icon="tabler:brand-whatsapp"
        >
          <template #actions>
            <AButton
              v-if="isSuperUser"
              variant="text"
              size="small"
              prepend-icon="tabler:photo"
              @click="goToMediaTools"
            >
              Media
            </AButton>
            <AButton
              v-if="isSuperUser"
              variant="text"
              size="small"
              prepend-icon="tabler:settings"
              @click="goToConfigClient"
            >
              Settings
            </AButton>
          </template>
          <template #meta>
            <ATag
              :text="currentEstablishment.name"
              color="brand"
              type="secondary"
              prepend-icon="tabler:building"
            />
          </template>
        </BspPageHeader>

        <section class="bsp-onboarding-section" aria-labelledby="onboarding-title">
          <div class="bsp-section-heading">
            <div>
              <span class="bsp-section-heading__eyebrow">Set up a channel</span>
              <h2 id="onboarding-title">Add a phone number or WABA</h2>
              <p>Choose the flow that matches the current status of your phone number.</p>
            </div>
          </div>

          <div class="bsp-action-grid">
            <v-card class="bsp-action-card" variant="flat">
              <span class="bsp-channel-icon bsp-channel-icon--primary" aria-hidden="true">
                <AIcon icon="tabler:device-mobile-plus" color="primary" size="22" />
              </span>
              <div class="bsp-action-card__copy">
                <h3>New phone number or migration</h3>
                <p>Create or select a WABA, register a new phone number, or migrate one from another BSP.</p>
              </div>
              <AButton
                color="blue"
                prepend-icon="tabler:plus"
                :disabled="isSignupActionDisabled"
                @click="launchWhatsAppSignup('cloud_api')"
              >
                Register phone number
              </AButton>
            </v-card>

            <v-card class="bsp-action-card" variant="flat">
              <span class="bsp-channel-icon bsp-channel-icon--whatsapp" aria-hidden="true">
                <AIcon icon="tabler:brand-whatsapp" color="success" size="22" />
              </span>
              <div class="bsp-action-card__copy">
                <h3>WhatsApp Business App</h3>
                <p>Connect a phone number active in the app and enable Coexistence with Cloud API.</p>
              </div>
              <AButton
                variant="outlined"
                color="blue"
                prepend-icon="tabler:link"
                :disabled="isSignupActionDisabled"
                @click="launchWhatsAppSignup('coexistence')"
              >
                Connect app
              </AButton>
            </v-card>
          </div>
        </section>

        <section class="bsp-section whatsapp-accounts-container" aria-labelledby="accounts-title">
          <div class="bsp-section-heading bsp-section-heading--toolbar">
            <div>
              <div class="bsp-section-heading__title-row">
                <h2 id="accounts-title">WhatsApp Business Accounts</h2>
                <ATag
                  :text="`${filteredAccounts.length} of ${whatsappBusinessAccounts.length}`"
                  color="info"
                  type="secondary"
                />
              </div>
              <p>Search for an account to manage its phone numbers, templates, and settings.</p>
            </div>
            <div class="bsp-toolbar">
              <AInput
                v-model="accountSearch"
                class="bsp-search-input"
                placeholder="Search by name or ID"
                prepend-inner-icon="tabler:search"
                clearable
              />
              <AButton
                variant="outlined"
                color="blue"
                prepend-icon="tabler:refresh"
                @click="SynchronizeWhatsappBusinessAccounts"
              >
                Synchronize
              </AButton>
            </div>
          </div>

          <div class="bsp-account-grid" v-if="filteredAccounts.length > 0">
            <v-card
              class="bsp-account-card"
              variant="flat"
              v-for="whatsappAccount in filteredAccounts"
              :key="whatsappAccount.id"
              tabindex="0"
              @click="goToWhatsappBusinessAccount(whatsappAccount.id)"
              @keydown.enter="goToWhatsappBusinessAccount(whatsappAccount.id)"
            >
              <span class="bsp-channel-icon bsp-channel-icon--whatsapp" aria-hidden="true">
                <AIcon icon="tabler:brand-whatsapp" color="success" size="20" />
              </span>
              <div class="whatsapp-account-info">
                <h3>{{ whatsappAccount.name }}</h3>
                <span>ID {{ whatsappAccount.id }}</span>
              </div>
              <AButton
                icon="tabler:chevron-right"
                variant="text"
                size="small"
                help-text="Open account"
                @click.stop="goToWhatsappBusinessAccount(whatsappAccount.id)"
              />
            </v-card>
          </div>

          <AEmptyViewCard
            v-else
            icon="tabler:brand-whatsapp"
            :title="accountSearch ? 'No accounts found' : 'No synchronized accounts yet'"
            :description="accountSearch ? 'Try another name or ID.' : 'Synchronize with Meta to load your WhatsApp Business accounts.'"
            :action-button-label="accountSearch ? undefined : 'Synchronize accounts'"
            @action="SynchronizeWhatsappBusinessAccounts"
          />
        </section>
      </div>

      <div class="no-client-view bsp-empty-page" v-else>
        <BspPageHeader
          :breadcrumbs="accountBreadcrumbs"
          title="Set up your establishment"
          description="Create an organization to start managing WhatsApp Business."
          eyebrow="First step"
          icon="tabler:building-plus"
        />
        <v-card class="bsp-form-card" variant="flat">
          <h2>New establishment</h2>
          <p>We'll use this name to identify your operation within the platform.</p>
          <AInput
            v-model="establishmentName"
            label="Establishment name"
            placeholder="E.g. Adereso Chile"
          />
          <AButton color="blue" block prepend-icon="tabler:plus" @click="createEstablishment">
            Create establishment
          </AButton>
        </v-card>
      </div>
    </div>
    <SpinnerLoader v-else />
    <SignupProgressModal
      :visible="showSignupProgressModal"
      :title="signupModalTitle"
      :description="signupModalDescription"
      :steps="signupSteps"
      :error-message="signupErrorMessage"
      :flow-status="signupFlowStatus"
      @close="closeSignupModal"
    />
  </div>
</template>

<script setup lang="ts">

import { useRouter, useRoute } from 'vue-router'
import AuthService from '../services/AuthService/AuthService'
import { onMounted, ref, computed } from 'vue'
import SpinnerLoader from '../components/loaders/SpinnerLoader.vue'
import SignupProgressModal from '../components/SignupProgressModal.vue'
import { User } from '../services/AuthService/types'
import { Establishment } from '../services/EstablishmentService/types'
import { WhatsappBusinessAccount } from '../services/AccountService/types'
import AccountService from '../services/AccountService/AccountService'
import { POSITION, useToast } from 'vue-toastification'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import MetaService from '@/services/MetaService/MetaService'
import { useEmbeddedSignup } from '@/composables/useEmbeddedSignup'
import BspPageHeader from '@/components/BspPageHeader.vue'
import { AButton, AEmptyViewCard, AIcon, AInput, ATag } from '@adereso-ai/vue-components'
import { normalizeSearchTerm } from '@/utils/search'

const router = useRouter()
const route = useRoute()
const toast = useToast()

const {
  signupFlowStatus,
  signupErrorMessage,
  signupSteps,
  isSignupActionDisabled,
  showSignupProgressModal,
  signupModalTitle,
  signupModalDescription,
  launchWhatsAppSignup,
  closeSignupModal
} = useEmbeddedSignup({
  async onSuccess () {
    await getAccounts()
  }
})

const loading = ref(true)
const currentUser = ref<User | undefined>(undefined)
const currentEstablishment = ref<Establishment | undefined>(undefined)
const establishmentName = ref('')
const whatsappBusinessAccounts = ref<Array<WhatsappBusinessAccount>>([])
const accountSearch = ref('')

const accountBreadcrumbs = [
  {
    title: 'WhatsApp Accounts',
    disabled: true,
    icon: 'tabler:brand-whatsapp'
  }
]

const filteredAccounts = computed(() => {
  const search = normalizeSearchTerm(accountSearch.value)
  if (!search) return whatsappBusinessAccounts.value
  return whatsappBusinessAccounts.value.filter((account) => {
    return account.name.toLocaleLowerCase().includes(search) || account.id.includes(search)
  })
})

const isSuperUser = computed(() => {
  return currentUser.value?.roles.includes('superuser')
})

async function goToWhatsappBusinessAccount (wabaId: string) {
  router.push({ name: 'whatsapp-business-account', params: { wabaId } })
}

async function goToMediaTools () {
  router.push({ name: 'media-tools' })
}

async function goToConfigClient () {
  router.push({ name: 'config-client' })
}

async function getAccounts () {
  const accountService = await AccountService.getInstance()
  try {
    whatsappBusinessAccounts.value = await accountService.getWhatsappBusinessAccounts()
  } catch (error) {
    // do nothing
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    const establishment = await establishmentService.getCurrentEstablishment()
    currentEstablishment.value = establishment
  } catch (error) {
    // do nothing
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value
  if (authService.currentUser.value == null) {
    router.push({ name: 'login', query: route.query })
  }
}

onMounted(async () => {
  await router.isReady()

  await getCurrentUser()

  await getEstablishment()

  if (currentEstablishment.value) {
    await getAccounts()
  }

  loading.value = false
})

async function createEstablishment () {
  if (establishmentName.value === '') {
    toastError('Establishment name is required')
    return
  }
  const establishmentService = await EstablishmentService.getInstance()
  await establishmentService.createEstablishment(establishmentName.value)
  router.push('/')
}

function toastError (errorMessage: string) {
  try {
    toast.error(String(errorMessage), {
      position: POSITION.BOTTOM_LEFT
    })
  } catch (error) {
    console.log('error on toast:', error)
  }
}

async function SynchronizeWhatsappBusinessAccounts () {
  loading.value = true
  const metaService = await MetaService.getInstance()
  await metaService.synchronizeWhatsappBusinessAccounts()
  await getAccounts()
  loading.value = false
}

</script>

<style scoped lang="scss">
/* Variables de Color */
$primary-color: #0052CC;
$secondary-color: #6B778C;
$success-color: #36B37E;
$danger-color: #FF5630;
$whatsapp-color: #25D366;
$background-color: #F4F5F7;
$text-color: #172B4D;
$border-color: #DFE1E6;

/* Importar Font Awesome */
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css');

/* Estilos Generales */
.home-container {
  padding: 20px;
  background-color: $background-color;
  color: $text-color;
  min-height: 100vh;

  .home {
    display: flex;
    flex-direction: column;
    gap: 30px;
    align-items: center;

    .client-view {
      width: 100%;
      max-width: 1200px;

      .welcome-section {
        text-align: center;
        margin-bottom: 20px;

        h1 {
          margin-bottom: 5px;
          font-size: 28px;
        }

        p {
          margin-top: 0;
          font-size: 18px;
          color: $secondary-color;
        }
      }

      .whatsapp-login {
        margin: 20px 0;
        text-align: center;

        .btn-primary {
          @extend .btn;
          background-color: $primary-color;
          border-color: $primary-color;

          i {
            margin-right: 10px;
            font-size: 20px;
          }
        }
      }

      .whatsapp-accounts-container {
        width: 100%;

        .whatsapp-accounts-list {
          margin-bottom: 20px;

          h2 {
            margin-bottom: 15px;
            font-size: 24px;
          }

          .whatsapp-account {
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: #fff;
            border: 1px solid $border-color;
            border-radius: 6px;
            padding: 15px;
            cursor: pointer;
            transition: background-color 0.2s, box-shadow 0.2s;

            &:hover {
              background-color: $background-color;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            .whatsapp-account-info {
              h3 {
                margin: 0;
                font-size: 20px;
              }

              p {
                margin: 5px 0 0 0;
                font-size: 14px;
                color: $secondary-color;
              }
            }

            .whatsapp-account-logo {
              i {
                font-size: 40px;
                color: $whatsapp-color;
              }
            }
          }
        }

        .whatsapp-synchronize-button {
          text-align: center;

          p {
            margin-bottom: 10px;
            color: $secondary-color;
          }

          .btn-secondary {
            @extend .btn;
            background-color: $secondary-color;
            border-color: $secondary-color;

            i {
              margin-right: 10px;
            }
          }
        }
      }

      .config-buttons {
        display: flex;
        gap: 20px;
        justify-content: center;
        margin-top: 20px;

        .btn-secondary {
          @extend .btn;
          background-color: $secondary-color;
          border-color: $secondary-color;

          i {
            margin-right: 10px;
          }
        }
      }
    }

    .no-client-view {
      text-align: center;

      .no-establishment-message {
        margin-bottom: 30px;

        h2 {
          margin-bottom: 10px;
          font-size: 24px;
        }

        p {
          margin: 0;
          color: $secondary-color;
        }
      }

      .create-establishment-form {
        background-color: #fff;
        border: 1px solid $border-color;
        border-radius: 6px;
        padding: 30px;
        max-width: 400px;
        margin: 0 auto;

        h2 {
          margin-top: 0;
          margin-bottom: 20px;
          font-size: 24px;
        }

        .form-group {
          margin-bottom: 20px;
          text-align: left;

          label {
            display: block;
            margin-bottom: 5px;
            font-weight: bold;
          }

          input {
            width: 100%;
            padding: 10px;
            border: 1px solid $border-color;
            border-radius: 4px;
            box-sizing: border-box;
            font-size: 14px;
          }
        }

        .btn-primary {
          @extend .btn;
          background-color: $primary-color;
          border-color: $primary-color;
          width: 100%;
          font-size: 16px;
        }
      }
    }
  }
}

.whatsapp-signup-section {
  background-color: #fff;
  border: 1px solid $border-color;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: center;

  h2 {
    margin: 0 0 16px;
    font-size: 20px;
  }
}

.whatsapp-signup-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;

  button {
    width: 400px;
  }
}

.signup-caption {
  color: $secondary-color;
  margin: 0 0 6px;
  max-width: 460px;
  text-align: center;
}

/* Estilos para Botones */
.btn {
  display: inline-block;
  font-weight: 500;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  padding: 10px 20px;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 4px;
  border: 1px solid transparent;
  color: #fff;
  background-color: $primary-color;
  border-color: $primary-color;
  text-decoration: none;

  &:hover {
    background-color: darken($primary-color, 5%);
    border-color: darken($primary-color, 5%);
  }

  &.btn-secondary {
    background-color: $secondary-color;
    border-color: $secondary-color;

    &:hover {
      background-color: darken($secondary-color, 5%);
      border-color: darken($secondary-color, 5%);
    }
  }

  &.btn-success {
    background-color: $success-color;
    border-color: $success-color;

    &:hover {
      background-color: darken($success-color, 5%);
      border-color: darken($success-color, 5%);
    }
  }

  &.btn-danger {
    background-color: $danger-color;
    border-color: $danger-color;

    &:hover {
      background-color: darken($danger-color, 5%);
      border-color: darken($danger-color, 5%);
    }
  }

  &.btn-whatsapp {
    background-color: $whatsapp-color;
    border-color: $whatsapp-color;

    &:hover {
      background-color: darken($whatsapp-color, 5%);
      border-color: darken($whatsapp-color, 5%);
    }
  }

  &.btn-link {
    background: none;
    border: none;
    color: $primary-color;
    padding: 0;
    font-weight: normal;

    &:hover {
      color: darken($primary-color, 5%);
      text-decoration: underline;
    }

    &:disabled {
      color: $secondary-color;
      cursor: not-allowed;
    }
  }
}
</style>
